import React, { useState } from 'react';

// ----- Types -----

interface UserCard {
  id: string;
  displayName: string;
  role: string;
  industry: string;
  photoUrl: string | null;
  connectionStatus: 'none' | 'pending' | 'connected';
}

interface RecommendationCard extends UserCard {
  rationale: string;
}

// ----- Mock data -----
const MOCK_USERS: UserCard[] = [];
const MOCK_RECOMMENDATIONS: RecommendationCard[] = [];

// ----- Main Page -----

/**
 * PeopleDiscoveryPage — discover and connect with other users.
 * Filter by industry, role, and tags. Shows Gail recommendations if available.
 */
export function PeopleDiscoveryPage(): React.JSX.Element {
  const [users, setUsers] = useState<UserCard[]>(MOCK_USERS);
  const [recommendations] = useState<RecommendationCard[]>(MOCK_RECOMMENDATIONS);
  const [industryFilter, setIndustryFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [tagsFilter, setTagsFilter] = useState('');

  function handleConnect(userId: string) {
    // TODO: call POST /connections
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, connectionStatus: 'pending' as const } : u))
    );
  }

  function handleApplyFilters(e: React.FormEvent) {
    e.preventDefault();
    // TODO: call GET /people with filters
  }

  return (
    <div className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text-primary sm:text-3xl">People</h1>
          <p className="mt-1 text-text-secondary">
            Discover professionals on a similar journey and grow your network.
          </p>
        </div>

        {/* Gail recommendations section */}
        {recommendations.length > 0 && (
          <section className="mb-8">
            <div className="mb-4 flex items-center gap-2">
              <span
                className="h-3 w-3 rounded-full bg-gail"
                aria-hidden="true"
              />
              <h2 className="text-lg font-semibold text-text-primary">
                Gail&apos;s Recommendations
              </h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recommendations.map((rec) => (
                <PersonCard
                  key={rec.id}
                  user={rec}
                  onConnect={() => handleConnect(rec.id)}
                  rationale={rec.rationale}
                />
              ))}
            </div>
          </section>
        )}

        <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
          {/* Filter panel */}
          <aside>
            <form
              onSubmit={handleApplyFilters}
              className="rounded-lg border border-border bg-surface-raised p-4"
            >
              <h2 className="mb-4 text-sm font-semibold text-text-primary">Filters</h2>

              <div className="mb-3">
                <label
                  htmlFor="industry-filter"
                  className="mb-1 block text-xs font-medium text-text-secondary"
                >
                  Industry
                </label>
                <input
                  id="industry-filter"
                  type="text"
                  value={industryFilter}
                  onChange={(e) => setIndustryFilter(e.target.value)}
                  placeholder="e.g. Technology"
                  className="w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
                />
              </div>

              <div className="mb-3">
                <label
                  htmlFor="role-filter"
                  className="mb-1 block text-xs font-medium text-text-secondary"
                >
                  Role
                </label>
                <input
                  id="role-filter"
                  type="text"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  placeholder="e.g. Product Manager"
                  className="w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="tags-filter"
                  className="mb-1 block text-xs font-medium text-text-secondary"
                >
                  Interest tags
                </label>
                <input
                  id="tags-filter"
                  type="text"
                  value={tagsFilter}
                  onChange={(e) => setTagsFilter(e.target.value)}
                  placeholder="e.g. leadership, coaching"
                  className="w-full rounded border border-border bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
                />
              </div>

              <button
                type="submit"
                className="w-full rounded bg-gail px-3 py-2 text-sm font-medium text-white hover:bg-gail-accent"
              >
                Apply Filters
              </button>
            </form>
          </aside>

          {/* User grid */}
          <section>
            {users.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-8 text-center">
                <p className="text-sm text-text-muted">
                  No people match your current filters. Try broadening your search.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {users.map((user) => (
                  <PersonCard key={user.id} user={user} onConnect={() => handleConnect(user.id)} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ----- Person Card -----

function PersonCard({
  user,
  onConnect,
  rationale,
}: {
  user: UserCard;
  onConnect: () => void;
  rationale?: string;
}) {
  return (
    <div className="flex flex-col rounded-lg border border-border bg-surface-raised p-4">
      <div className="mb-3 flex items-center gap-3">
        {user.photoUrl ? (
          <img
            src={user.photoUrl}
            alt=""
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-muted">
            <UserIcon />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="truncate text-sm font-medium text-text-primary">{user.displayName}</p>
          <p className="truncate text-xs text-text-muted">{user.role}</p>
        </div>
      </div>

      <p className="mb-3 text-xs text-text-secondary">{user.industry}</p>

      {/* Rationale chip (Gail recommendations) */}
      {rationale && (
        <p className="mb-3 rounded bg-gail-light px-2 py-1 text-xs text-gail">
          {rationale}
        </p>
      )}

      {/* Connect button */}
      <button
        onClick={onConnect}
        disabled={user.connectionStatus !== 'none'}
        className={`mt-auto w-full rounded px-3 py-1.5 text-sm font-medium transition ${
          user.connectionStatus === 'connected'
            ? 'cursor-default bg-surface-muted text-text-muted'
            : user.connectionStatus === 'pending'
              ? 'cursor-wait bg-surface-muted text-text-secondary'
              : 'bg-gail text-white hover:bg-gail-accent'
        }`}
      >
        {user.connectionStatus === 'connected'
          ? 'Connected'
          : user.connectionStatus === 'pending'
            ? 'Pending…'
            : 'Connect'}
      </button>
    </div>
  );
}

// ----- Icons -----

function UserIcon() {
  return (
    <svg className="h-5 w-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </svg>
  );
}
